/**
 * Ritual Bar — Production booking flow
 * State machine: ritual → date → elevate → time → checkout
 *
 * Live data:
 *   window.HOLY_BOOKING_SLOTS  — booking_slot metaobjects piped from Shopify
 *   window.HOLY_BLACKOUTS      — blackout_period metaobjects piped from Shopify
 *   window.HOLY_BOOKING_CONFIG — variant IDs + cart routes
 *
 * Commerce:
 *   Time-pick POSTs the Ritual deposit variant (+ selected elevation deposits)
 *   to /cart/add.js with line item properties (_slot_id, _slot_date, _slot_start,
 *   _service, _pay_mode='upfront') — then redirects to /checkout (Shopify Flow
 *   workflow #1 binds the slot on order_created).
 */

(() => {
  'use strict';

  const CONFIG = window.HOLY_BOOKING_CONFIG || {
    variants: {
      ritual_deposit:        52356985487672,
      elevated_deposit:      52356985553208,
      dermaplaning_deposit:  52420836622648,
      peel_deposit:          52420836884792,
      microneedling_deposit: 52420837409080,
    },
    cart_add_url: '/cart/add.js',
    cart_clear_url: '/cart/clear.js',
    checkout_url: '/checkout',
    bar_link: '/pages/bar-daybreak',
  };

  const ELEVATIONS = [
    { slug: 'dry_brushing',  label: 'Dry brushing',  deposit: 0,     balance: 0,     total: 0,   duration_delta: 5,  default_selected: true,
      helper: 'Wakes the lymphatic surface. Sensitive skin can opt out.', variant_key: null },
    { slug: 'dermaplaning',  label: 'Dermaplaning',  deposit: 20,    balance: 20,    total: 40,  duration_delta: 10, default_selected: false,
      helper: "Smooths the skin's surface. The next layers settle deeper.", variant_key: 'dermaplaning_deposit' },
    { slug: 'chemical_peel', label: 'Chemical peel', deposit: 37.50, balance: 37.50, total: 75,  duration_delta: 15, default_selected: false,
      helper: 'Resurfaces the top layer. Tone clears over the days after.', variant_key: 'peel_deposit' },
    { slug: 'microneedling', label: 'Microneedling', deposit: 57.50, balance: 57.50, total: 115, duration_delta: 20, default_selected: false,
      helper: 'Touches collagen at depth. Structure rebuilds over weeks.', variant_key: 'microneedling_deposit' },
  ];

  const SERVICE_PRICE = 220;
  const DEPOSIT_AMOUNT = 110;
  const URGENT_THRESHOLD_HOURS = 48;
  const MAX_HORIZON_DAYS = 120;
  const OPEN_DOW = new Set([0, 2, 3, 4]); // Sun, Tue, Wed, Thu

  // Live slots from Shopify metaobjects — fall back to a 90-day window of mocks
  // so the page still renders if the metaobject query returned nothing.
  const SLOTS = (() => {
    const live = (window.HOLY_BOOKING_SLOTS || []).filter((s) => s && s.date && s.start_time);
    if (live.length > 0) {
      return live.map((s) => ({
        id: s.gid || s.handle || `${s.date}-${s.start_time}`,
        gid: s.gid || null,
        date: s.date,
        start_time: (s.start_time || '').slice(0, 5),  // HH:MM
        duration_min: s.duration_min || 120,
        status: s.status || 'available',
      }));
    }
    console.warn('[holy-booking] No live booking_slot metaobjects found — falling back to seeded 90-day mock window.');
    const seeded = [];
    for (let i = 0; i < 90; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dow = d.getDay();
      if (!OPEN_DOW.has(dow)) continue;
      const ds = ymd(d);
      seeded.push({ id: `mock-${ds}-1100`, gid: null, date: ds, start_time: '11:00', duration_min: 120, status: 'available' });
      seeded.push({ id: `mock-${ds}-1300`, gid: null, date: ds, start_time: '13:00', duration_min: 120, status: 'available' });
    }
    return seeded;
  })();

  // Blackouts from Shopify — expand each [start, end] into a Set of YYYY-MM-DD strings
  const BLACKOUT_DATES = (() => {
    const dates = new Set();
    const messages = new Map();
    for (const b of (window.HOLY_BLACKOUTS || [])) {
      if (!b || !b.start_date) continue;
      const start = new Date(b.start_date + 'T12:00:00');
      const end = new Date((b.end_date || b.start_date) + 'T12:00:00');
      const cursor = new Date(start);
      while (cursor <= end) {
        const k = ymd(cursor);
        dates.add(k);
        if (b.customer_message) messages.set(k, b.customer_message);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return { dates, messages };
  })();

  // ───────── State ─────────

  const root = document.querySelector('[data-rb-app]');
  if (!root) return;

  const state = {
    cursorMonth: new Date(),
    selectedDate: null,
    selectedSlot: null,
    selectedElevations: ELEVATIONS.filter((e) => e.default_selected).map((e) => e.slug),
    stage: 'ritual',
    slotsByDate: new Map(),
  };
  for (const s of SLOTS) {
    if (!state.slotsByDate.has(s.date)) state.slotsByDate.set(s.date, []);
    state.slotsByDate.get(s.date).push(s);
  }

  // ───────── Helpers ─────────

  function ymd(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
  function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
  function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
  function monthLabel(d) { return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }); }

  function currentWeekSunday() {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay());
    sunday.setHours(0, 0, 0, 0);
    return sunday;
  }
  function isSameMonth(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth(); }
  function formatTime(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'pm' : 'am';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${String(m).padStart(2, '0')}${period}`;
  }
  function formatLongDate(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    });
  }
  function formatShortDate(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }
  function formatMoney(n) { return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`; }
  function hoursUntil(dateStr, timeStr) {
    return (new Date(`${dateStr}T${timeStr}:00`).getTime() - Date.now()) / 36e5;
  }
  function dayState(dateStr) {
    const today = ymd(new Date());
    if (dateStr < today) return 'past';
    if (BLACKOUT_DATES.dates.has(dateStr)) return 'unavailable';
    const dow = new Date(dateStr + 'T12:00:00').getDay();
    if (!OPEN_DOW.has(dow)) return 'closed';
    const slots = state.slotsByDate.get(dateStr) || [];
    const open = slots.filter((s) => s.status === 'available');
    if (open.length === 0) return 'closed';
    return 'open';
  }
  function elevationBySlug(s) { return ELEVATIONS.find((e) => e.slug === s); }
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ───────── State machine ─────────

  const STAGE_ORDER = ['ritual', 'date', 'elevate', 'time'];
  // Mobile = sequential 4-stage; Desktop = collapsed (elevate+time combined into final step).
  // Pre-payment max is intentionally <100% — the final fill belongs to Shopify checkout.
  const STAGE_PROGRESS_MOBILE  = { ritual: 5, date: 25, elevate: 50, time: 75 };
  const STAGE_PROGRESS_DESKTOP = { ritual: 5, date: 35, elevate: 70, time: 70 };
  // Desktop step indicator
  const STAGE_INDICATOR = {
    ritual:  'Welcome',
    date:    'Step 1 of 2',
    elevate: 'Step 2 of 2',
    time:    'Step 2 of 2',
  };
  function isDesktopViewport() { return window.innerWidth >= 1024; }

  function goto(stage) {
    state.stage = stage;
    root.dataset.stage = stage;

    // Pane visibility — on desktop, 'time' stage reuses the elevate state-pane (combined editorial).
    // Instant cuts between stages — no fade animation.
    const visualStage = (isDesktopViewport() && stage === 'time') ? 'elevate' : stage;
    document.querySelectorAll('[data-state]').forEach((el) => {
      const match = el.dataset.state === visualStage;
      el.classList.toggle('hidden', !match);
    });

    // Progress — viewport-aware (mobile uses 4-stage scale; desktop collapses elevate+time)
    const progressTable = isDesktopViewport() ? STAGE_PROGRESS_DESKTOP : STAGE_PROGRESS_MOBILE;
    const pct = progressTable[stage] || 0;
    document.querySelectorAll('[data-rb-progress]').forEach((bar) => {
      bar.style.width = `${pct}%`;
    });
    const PIE_CIRCUMFERENCE = 106.81;
    const offset = PIE_CIRCUMFERENCE * (1 - pct / 100);
    document.querySelectorAll('[data-rb-pie-fill]').forEach((el) => {
      el.style.strokeDashoffset = String(offset);
    });

    // Step indicator (desktop)
    const stepIndicator = document.querySelector('[data-rb-step-indicator]');
    if (stepIndicator) stepIndicator.textContent = STAGE_INDICATOR[stage] || '';

    // Render dynamic content. On desktop, 'elevate' also renders time slots (combined view).
    if (stage === 'date')    renderCalendar();
    if (stage === 'elevate') {
      renderElevations();
      if (isDesktopViewport()) renderTimePane();
    }
    if (stage === 'time')    renderTimePane();

    renderSummary();
    renderActionBars();
    updateRailWizard(stage);

    // Scroll to top — triple-pass to defeat iOS Safari momentum + late layout settles.
    // 1) immediate (before any further paint)
    // 2) after next paint (rAF — most layout settled)
    // 3) after next macrotask (catches late async layout shifts)
    scrollToTop();
    requestAnimationFrame(scrollToTop);
    setTimeout(scrollToTop, 0);
  }

  function scrollToTop() {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  // Back affordance — walks the stage stack in reverse; only exits booking when
  // already on the first stage (ritual).
  function goBack() {
    const idx = STAGE_ORDER.indexOf(state.stage);
    if (idx <= 0) {
      window.location.href = '/pages/bar-daybreak';
    } else {
      goto(STAGE_ORDER[idx - 1]);
    }
  }

  // ───────── Calendar ─────────

  function renderCalendar() {
    // Mobile main-pane calendar (visible <lg) + desktop rail calendar (visible ≥lg) — kept in sync
    renderCalendarInto({
      grid:    document.querySelector('[data-rb-cal-grid]'),
      monthEl: document.querySelector('[data-rb-cal-month]'),
      prevBtn: document.querySelector('[data-rb-cal-prev]'),
      nextBtn: document.querySelector('[data-rb-cal-next]'),
    });
    renderCalendarInto({
      grid:    document.querySelector('[data-rb-rail-cal-grid]'),
      monthEl: document.querySelector('[data-rb-rail-cal-month]'),
      prevBtn: null,  // rail prev/next handled separately
      nextBtn: null,
    });
  }

  function renderCalendarInto({ grid, monthEl, prevBtn, nextBtn }) {
    if (!grid || !monthEl) return;

    monthEl.textContent = monthLabel(state.cursorMonth);

    const today = new Date();
    const horizon = addDays(today, MAX_HORIZON_DAYS);
    const cursorIsCurrentMonth = isSameMonth(state.cursorMonth, today);

    if (prevBtn) prevBtn.disabled = state.cursorMonth <= startOfMonth(today);
    if (nextBtn) nextBtn.disabled = endOfMonth(state.cursorMonth) >= horizon;

    const start = cursorIsCurrentMonth ? currentWeekSunday() : startOfMonth(state.cursorMonth);
    const end = endOfMonth(state.cursorMonth);
    const startWeekday = cursorIsCurrentMonth ? 0 : start.getDay();

    grid.innerHTML = '';

    for (let i = 0; i < startWeekday; i++) {
      const empty = document.createElement('div');
      empty.setAttribute('aria-hidden', 'true');
      grid.appendChild(empty);
    }

    const cursor = new Date(start);
    while (cursor <= end) {
      const dateStr = ymd(cursor);
      const st = dayState(dateStr);
      const interactive = st === 'open';

      const cell = document.createElement(interactive ? 'button' : 'div');
      cell.dataset.date = dateStr;
      cell.textContent = String(cursor.getDate());
      cell.classList.add('rb-tile', 'rb-tile-cal');

      if (interactive) {
        cell.type = 'button';
        if (dateStr === state.selectedDate) cell.classList.add('is-selected');
        cell.setAttribute('aria-label', formatLongDate(dateStr));
        cell.addEventListener('click', (e) => {
          // Stop bubble so the parent rail-step's navigation handler doesn't
          // immediately revert state (it would catch this click and goto('date'),
          // overwriting the goto('elevate') from selectDate).
          e.stopPropagation();
          selectDate(dateStr);
        });
      } else {
        cell.classList.add('rb-tile-disabled');
        cell.setAttribute('aria-disabled', 'true');
      }
      grid.appendChild(cell);
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  function selectDate(dateStr) {
    state.selectedDate = dateStr;
    state.selectedSlot = null;
    renderCalendar();
    // Advance immediately — the visual confirmation should come from the
    // elevate pane sliding in, not from a delay where the date pane lingers.
    goto('elevate');
  }

  // Pre-wire month nav (events fire even if pane not rendered yet) — both main + rail.
  // stopPropagation on the rail variants so they don't bubble to the rail-step click handler.
  function calPrev(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    state.cursorMonth = new Date(state.cursorMonth.getFullYear(), state.cursorMonth.getMonth() - 1, 1);
    renderCalendar();
  }
  function calNext(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    state.cursorMonth = new Date(state.cursorMonth.getFullYear(), state.cursorMonth.getMonth() + 1, 1);
    renderCalendar();
  }
  document.querySelector('[data-rb-cal-prev]')?.addEventListener('click', calPrev);
  document.querySelector('[data-rb-cal-next]')?.addEventListener('click', calNext);
  document.querySelector('[data-rb-rail-cal-prev]')?.addEventListener('click', calPrev);
  document.querySelector('[data-rb-rail-cal-next]')?.addEventListener('click', calNext);

  // ───────── Elevations ─────────

  function renderElevations() {
    // Render into both main + rail elevation lists; same factory for cards.
    renderElevationsInto(document.querySelector('[data-rb-elevations]'));
    renderElevationsInto(document.querySelector('[data-rb-rail-elevations]'));
    updateIntenseComboWarning();
  }

  function renderElevationsInto(list) {
    if (!list) return;
    list.innerHTML = '';
    const hero = ELEVATIONS.find((e) => e.slug === 'dry_brushing');
    const others = ELEVATIONS.filter((e) => e.slug !== 'dry_brushing');
    if (hero) list.appendChild(renderElevationCard(hero, true));
    for (const elev of others) list.appendChild(renderElevationCard(elev, false));
  }

  function renderElevationCard(elev, isHero) {
    const isSelected = state.selectedElevations.includes(elev.slug);
    const li = document.createElement('li');
    li.dataset.slug = elev.slug;
    li.className = ['rb-elev', isHero ? 'rb-elev-hero' : '', isSelected ? 'is-selected' : '']
      .filter(Boolean)
      .join(' ');

    const priceHTML = elev.deposit === 0
      ? `<span class="rb-elev-price-included">included</span>`
      : `<span class="rb-elev-price">+${formatMoney(elev.total)}${
          elev.duration_delta > 0
            ? `<span class="rb-elev-price-sub">+ ${elev.duration_delta} min</span>`
            : ''
        }</span>`;

    li.innerHTML = `
      <span class="rb-elev-check">
        <input type="checkbox" class="w-[18px] h-[18px] cursor-pointer" style="accent-color:#C9A36D" ${isSelected ? 'checked' : ''} aria-label="Toggle ${elev.label}">
      </span>
      <div class="rb-elev-body">
        <p class="rb-elev-label">${elev.label}</p>
        ${elev.helper ? `<p class="rb-elev-helper">${elev.helper}</p>` : ''}
      </div>
      <div class="rb-elev-price-wrap">${priceHTML}</div>
    `;

    const check = li.querySelector('input');
    li.addEventListener('click', (e) => {
      if (e.target !== check) check.checked = !check.checked;
      toggleElevation(elev.slug, check.checked);
      // Reflect selection on the card itself so the in-palette tint is immediate
      li.classList.toggle('is-selected', check.checked);
    });

    return li;
  }

  function toggleElevation(slug, on) {
    if (on && !state.selectedElevations.includes(slug)) state.selectedElevations.push(slug);
    else if (!on) state.selectedElevations = state.selectedElevations.filter((s) => s !== slug);
    renderSummary();
    renderActionBars();
    updateIntenseComboWarning();
    // Keep both elevation lists in selection-sync (mirror checkboxes across main + rail)
    document.querySelectorAll(`.rb-elev[data-slug="${slug}"]`).forEach((li) => {
      li.classList.toggle('is-selected', on);
      const cb = li.querySelector('input[type="checkbox"]');
      if (cb) cb.checked = on;
    });
  }

  function updateIntenseComboWarning() {
    const has = (s) => state.selectedElevations.includes(s);
    const intense = (has('chemical_peel') && has('dermaplaning')) ||
                    (has('chemical_peel') && has('microneedling'));
    const shortMsg = 'These two together are intense. Your guide will adapt or recommend spacing.';
    const longMsg = 'These two together are intense and need recovery time. Your guide will adapt the ritual or recommend spacing them across visits.';
    document.querySelectorAll('[data-rb-elevate-warning]').forEach((warn) => {
      warn.textContent = longMsg;
      warn.classList.toggle('hidden', !intense);
    });
    document.querySelectorAll('[data-rb-rail-elevate-warning]').forEach((warn) => {
      warn.textContent = shortMsg;
      warn.classList.toggle('hidden', !intense);
    });
  }

  // ───────── Time ─────────

  function timeBucket(hhmm) {
    const [h] = hhmm.split(':').map(Number);
    if (h < 12) return 'Morning';
    if (h < 17) return 'Afternoon';
    return 'Evening';
  }

  function renderTimePane() {
    const anchor = document.querySelector('[data-rb-time-anchor]');
    if (anchor && state.selectedDate) anchor.textContent = formatLongDate(state.selectedDate);

    renderTimeSlotsInto(document.querySelector('[data-rb-slot-grid]'));
    renderTimeSlotsInto(document.querySelector('[data-rb-rail-slot-grid]'));
  }

  function renderTimeSlotsInto(grid) {
    if (!grid) return;
    const slots = (state.slotsByDate.get(state.selectedDate) || [])
      .filter((s) => s.status === 'available')
      .sort((a, b) => a.start_time.localeCompare(b.start_time));

    grid.innerHTML = '';
    for (const slot of slots) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rb-tile rb-tile-time';
      btn.dataset.slotId = slot.id;
      btn.innerHTML = `
        <span class="rb-tile-primary">${timeBucket(slot.start_time)}</span>
        <span class="rb-tile-sub">${formatTime(slot.start_time)}</span>
      `;
      btn.addEventListener('click', () => {
        state.selectedSlot = slot;
        document.querySelectorAll(`.rb-tile-time[data-slot-id]`).forEach((el) => {
          el.classList.toggle('is-selected', el.dataset.slotId === slot.id);
        });
        // Show loading overlay immediately so user gets feedback while we
        // hit /cart/clear, /cart/add, and Shopify checkout boots.
        showCheckoutOverlay();
        commitBooking(slot);
      });
      grid.appendChild(btn);
    }
  }

  // ───────── Summary rail ─────────

  function renderSummary() {
    // Rail wizard: per-step value labels (collapsed-state summaries)
    const dateValueEl = document.querySelector('[data-rail-step-value="date"]');
    if (dateValueEl) dateValueEl.textContent = state.selectedDate ? formatShortDate(state.selectedDate) : '';

    const elevValueEl = document.querySelector('[data-rail-step-value="elevate"]');
    if (elevValueEl) {
      const picked = state.selectedElevations.map((s) => elevationBySlug(s)?.label).filter(Boolean);
      elevValueEl.textContent = picked.length === 0
        ? 'None'
        : picked.length === 1
          ? picked[0]
          : `${picked[0]} + ${picked.length - 1} more`;
    }

    const timeValueEl = document.querySelector('[data-rail-step-value="time"]');
    if (timeValueEl) timeValueEl.textContent = state.selectedSlot
      ? `${timeBucket(state.selectedSlot.start_time)} · ${formatTime(state.selectedSlot.start_time)}`
      : '';

    // Rail bottom totals — show once any pick exists
    const railTotalsEl = document.querySelector('[data-rb-rail-totals]');
    const railTodayEl = document.querySelector('[data-rb-rail-today]');
    const railBarEl = document.querySelector('[data-rb-rail-bar]');
    const hasPicks = state.selectedDate || state.selectedSlot ||
                     state.selectedElevations.some((s) => s !== 'dry_brushing');
    const todayTotal = DEPOSIT_AMOUNT + state.selectedElevations.reduce(
      (sum, s) => sum + (elevationBySlug(s)?.deposit || 0), 0
    );
    const barTotal = (SERVICE_PRICE - DEPOSIT_AMOUNT) + state.selectedElevations.reduce(
      (sum, s) => sum + (elevationBySlug(s)?.balance || 0), 0
    );
    if (railTodayEl) railTodayEl.textContent = formatMoney(todayTotal);
    if (railBarEl) railBarEl.textContent = formatMoney(barTotal);
    if (railTotalsEl) railTotalsEl.hidden = !hasPicks;
  }

  // ───────── Rail wizard step-state manager ─────────
  // On RITUAL stage, the first wizard step (date) is ACTIVE — eye goes there.
  // On DESKTOP, once a day is picked, BOTH elevate and time are active concurrently
  // (elevations are optional; time-pick commits → checkout).
  function updateRailWizard(stage) {
    const steps = ['date', 'elevate', 'time'];
    const isDesktop = isDesktopViewport();
    const isCombined = isDesktop && (stage === 'elevate' || stage === 'time');
    const readyIdx = stage === 'ritual' ? 0 : steps.indexOf(stage);

    steps.forEach((step, i) => {
      const stepEl = document.querySelector(`[data-rail-step="${step}"]`);
      if (!stepEl) return;
      stepEl.classList.remove('is-locked', 'is-active', 'is-completed');

      if (isCombined) {
        // Date completed; elevate + time both active (parallel)
        if (step === 'date') stepEl.classList.add('is-completed');
        else stepEl.classList.add('is-active');
      } else {
        if (i < readyIdx) stepEl.classList.add('is-completed');
        else if (i === readyIdx) stepEl.classList.add('is-active');
        else stepEl.classList.add('is-locked');
      }
    });

    // Bottom totals strip: visible when picks accumulate
    const railTotalsEl = document.querySelector('[data-rb-rail-totals]');
    const hasPicks = state.selectedDate || state.selectedSlot ||
                     state.selectedElevations.some((s) => s !== 'dry_brushing');
    if (railTotalsEl) railTotalsEl.hidden = !hasPicks;

    // Bottom wrapper: hidden unless there are totals to display
    const railBottomEl = document.querySelector('[data-rb-rail-bottom]');
    if (railBottomEl) railBottomEl.hidden = !hasPicks;
  }

  // Rail step heads + "Edit" buttons — both jump to that stage.
  // On desktop combined state (elevate+time both active), clicking either active
  // step head is a no-op — they're both visible already; only Date Edit navigates back.
  document.querySelectorAll('[data-rail-step]').forEach((stepEl) => {
    stepEl.addEventListener('click', (e) => {
      if (stepEl.classList.contains('is-locked')) return;
      const target = stepEl.dataset.railStep;
      if (isDesktopViewport() && (state.stage === 'elevate' || state.stage === 'time')) {
        if (target === 'elevate' || target === 'time') return;
      }
      if (target) goto(target);
    });
  });
  document.querySelectorAll('[data-rail-step-edit]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const target = btn.dataset.railStepEdit;
      if (target) goto(target);
    });
  });

  // ───────── Action bars (sticky bottom + desktop floating) ─────────

  function renderActionBars() {
    const stage = state.stage;
    const stickyLine = document.querySelector('[data-rb-sticky-line]');
    const stickyLabel = document.querySelector('[data-rb-sticky-label]');
    const desktopLine = document.querySelector('[data-rb-desktop-line]');
    const desktopLabel = document.querySelector('[data-rb-desktop-label]');

    // Total line — same math both bars
    const todayTotal = DEPOSIT_AMOUNT + state.selectedElevations.reduce(
      (sum, s) => sum + (elevationBySlug(s)?.deposit || 0), 0
    );
    const barTotal = (SERVICE_PRICE - DEPOSIT_AMOUNT) + state.selectedElevations.reduce(
      (sum, s) => sum + (elevationBySlug(s)?.balance || 0), 0
    );
    const totalLine = `${formatMoney(todayTotal)} today · ${formatMoney(barTotal)} at the bar`;
    if (stickyLine) stickyLine.textContent = totalLine;
    if (desktopLine) desktopLine.textContent = totalLine;

    // Sticky CTA per stage. Body CTA on RITUAL says "Ritual Awaits" (ritual is subject);
    // sticky CTA says "Make time" (user is subject — the act of carving out the time).
    // Subjects split cleanly; brand voice holds.
    // Mobile sticky label — unchanged "Make time" register
    let label = 'Make time';
    if (stage === 'ritual')  label = 'Make time';
    if (stage === 'date')    label = state.selectedDate ? 'Continue' : 'Pick a day';
    if (stage === 'elevate') label = 'Find a time';
    if (stage === 'time')    label = state.selectedSlot ? 'To payment' : 'Pick a time';
    if (stickyLabel) stickyLabel.textContent = label;
  }

  // Sticky CTA (mobile) click handler — desktop carries action via rail step clicks + rail Continue
  function handleAdvance() {
    const stage = state.stage;
    if (stage === 'ritual')  goto('date');
    else if (stage === 'date') { if (state.selectedDate) goto('elevate'); }
    else if (stage === 'elevate') goto('time');
    else if (stage === 'time') {
      if (state.selectedSlot) commitBooking(state.selectedSlot);
    }
  }

  // ───────── Checkout loading overlay ─────────
  // Mounted immediately on time-tile click so user gets feedback while we
  // hit /cart/clear, /cart/add, then Shopify-checkout-render. Without this,
  // ~2s of dead air with no signal anything is happening.
  function showCheckoutOverlay() {
    if (document.querySelector('.rb-checkout-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'rb-checkout-overlay';
    overlay.innerHTML = `
      <div class="rb-checkout-overlay-inner">
        <span class="rb-checkout-overlay-mark" aria-hidden="true">ø</span>
        <p class="rb-checkout-overlay-title">Your ritual is set.</p>
        <p class="rb-checkout-overlay-sub">A few hours of quiet to come — payment opens next.</p>
      </div>
    `;
    document.body.appendChild(overlay);
  }
  function hideCheckoutOverlay() {
    const overlay = document.querySelector('.rb-checkout-overlay');
    if (overlay) overlay.remove();
  }

  // ───────── Commerce commit ─────────
  // Cart adds: Ritual deposit ($110) + any selected elevation deposits.
  // Properties carry slot context for Flow workflow #1 to bind the slot on
  // order_created. Then bounce straight to Shopify-native /checkout.
  async function commitBooking(slot) {
    if (!slot) return;
    const ctaButtons = document.querySelectorAll('[data-rb-sticky-cta], [data-rb-rail-cta]');
    ctaButtons.forEach((b) => { b.disabled = true; });

    // Property keys MUST match /16_axis/engine/lib/process-booking.js exactly:
    //   _slot_id · _slot_date · _slot_start_time · _slot_duration_min · _slot_service_title
    // Mismatched keys cause silent binding failure: Klaviyo event fires but
    // booking_slot stays "available" and Google Calendar event never lands.
    const slotId = slot.gid || slot.id;
    const baseProps = {
      _slot_id:             slotId,
      _slot_date:           slot.date,
      _slot_start_time:     slot.start_time,
      _slot_duration_min:   String(slot.duration_min || 120),
      _slot_service_title:  'The Ritual',
      _pay_mode:            'upfront',
    };

    const items = [
      { id: CONFIG.variants.ritual_deposit, quantity: 1, properties: baseProps },
    ];

    for (const slug of state.selectedElevations) {
      const elev = elevationBySlug(slug);
      if (!elev || !elev.variant_key) continue;  // dry_brushing has no variant
      const variantId = CONFIG.variants[elev.variant_key];
      if (!variantId) continue;
      items.push({
        id: variantId,
        quantity: 1,
        properties: {
          _slot_id:        slotId,
          _booking:        'elevation',
          _elevation_slug: slug,
        },
      });
    }

    // Fire cart clear in parallel — don't wait for it. /cart/add.js is serialized
    // on Shopify's side, so by the time the add hits the cart endpoint, the clear
    // has already completed. Saves ~300ms vs sequential.
    fetch(CONFIG.cart_clear_url || '/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } })
      .catch(() => {});  // intentionally swallow — non-fatal

    try {
      const resp = await fetch(CONFIG.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`cart add failed: ${resp.status} ${text}`);
      }
    } catch (e) {
      console.error('[holy-booking] commit failed:', e);
      ctaButtons.forEach((b) => { b.disabled = false; });
      hideCheckoutOverlay();
      alert("We couldn't hold that time just now — refresh and try again, or message ritual-bar@holyritual.com if it persists.");
      return;
    }

    window.location.href = CONFIG.checkout_url || '/checkout';
  }
  document.querySelector('[data-rb-sticky-cta]')?.addEventListener('click', handleAdvance);

  // Generic advance triggers — any element with data-rb-advance="<stage>" jumps to that stage.
  document.querySelectorAll('[data-rb-advance]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.rbAdvance;
      if (target) goto(target);
    });
  });

  // "Start" pie wrapper — clickable on RITUAL stage to enter the calendar step
  document.querySelector('[data-rb-pie-start]')?.addEventListener('click', () => {
    if (state.stage === 'ritual') goto('date');
  });

  // Editorial back link on desktop left pane — walks one stage backward
  document.querySelectorAll('[data-rb-back-stage]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = STAGE_ORDER.indexOf(state.stage);
      if (idx > 0) goto(STAGE_ORDER[idx - 1]);
    });
  });

  // Rail calendar month navigation — wire prev/next inside the rail's interactive calendar
  document.querySelector('[data-rb-rail-cal-prev]')?.addEventListener('click', () => {
    state.cursorMonth = new Date(state.cursorMonth.getFullYear(), state.cursorMonth.getMonth() - 1, 1);
    renderCalendar();
  });
  document.querySelector('[data-rb-rail-cal-next]')?.addEventListener('click', () => {
    state.cursorMonth = new Date(state.cursorMonth.getFullYear(), state.cursorMonth.getMonth() + 1, 1);
    renderCalendar();
  });

  // Header back — walks stages in reverse
  document.querySelector('[data-rb-back]')?.addEventListener('click', goBack);

  // ───────── Init ─────────

  function init() {
    // Pre-populate so rail previews + main pane content are ready before first transition
    renderCalendar();
    renderElevations();
    renderSummary();
    renderActionBars();
    goto('ritual');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
